<script setup lang="ts">
/**
 * The header bar (design prototype `<header>`, start version): the drawer button and the brand
 * with "powered by" on phones, from md up the customer's name or "Mitarbeiterbereich" (B-13,
 * C-11), "Anmelden" for guests, the user menu when signed in. Guests also get "Für die Beta bewerben",
 * a button like "Anmelden" that leads to the application page `/beta` (ADR-143 addendum 3, TL
 * 2026-10-28; in the alpha release in place of "Zugang anfragen", TL 2026-10-27). The drawer
 * button is exposed (`menuButton`), so the drawer can give the focus back to it (PF9).
 */
import { computed, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import type { SessionResponse } from "@ai-platform/contracts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import BrandLogo from "./BrandLogo.vue";
import UserMenu from "./UserMenu.vue";

const props = defineProps<{
  session: SessionResponse | null;
  /** true while the session is not known yet; the right side stays empty instead of flickering */
  pending: boolean;
}>();

const emit = defineEmits<{ openMenu: []; login: [] }>();

const router = useRouter();

/** The customer the session works for, else the staff area; nothing for guests. */
const who = computed(() => props.session?.customer?.name ?? (props.session?.staffRole ? "Mitarbeiterbereich" : null));

/** "Menü öffnen", where the focus goes back when the drawer closes and nothing else had it. */
const menuButton = useTemplateRef<HTMLButtonElement>("menuButton");
defineExpose({ menuButton });
</script>

<template>
  <header
    class="border-line bg-glass px-safety sticky top-0 z-(--z-sticky) flex h-(--size-header) items-center gap-3 border-b backdrop-blur md:px-8"
  >
    <button
      ref="menuButton"
      type="button"
      class="rounded-control text-text-head hover:bg-fill focus-visible:outline-focus inline-flex size-(--size-button) cursor-pointer items-center justify-center border-0 bg-transparent focus-visible:outline-2 focus-visible:outline-offset-2 md:hidden"
      aria-label="Menü öffnen"
      @click="emit('openMenu')"
    >
      <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M4 7h16M4 12h16M4 17h16" stroke-linecap="round" />
      </svg>
    </button>
    <RouterLink :to="{ name: 'home' }" class="text-text-head inline-flex items-center md:hidden">
      <BrandLogo class="h-13.5" />
    </RouterLink>
    <p v-if="who" class="text-ui text-text-head m-0 truncate font-semibold max-md:hidden" data-who>{{ who }}</p>

    <div v-if="!props.pending" class="ml-auto flex items-center gap-3">
      <UserMenu v-if="props.session" :session="props.session" />
      <template v-else>
        <PixButton
          outline
          class="text-ui min-h-(--size-field) px-4 font-semibold max-sm:hidden"
          data-action="apply"
          @click="router.push({ name: 'application' })"
        >
          Für die Beta bewerben
        </PixButton>
        <PixButton class="text-ui min-h-(--size-field) px-4 font-semibold" @click="emit('login')">Anmelden</PixButton>
      </template>
    </div>
  </header>
</template>
